import { useEffect, useRef, useState } from "react";
import { PulseLoader } from "react-spinners";
import MarkdownDisplay from "./MarkdownDisplay";
import { ProgressBar } from "react-loader-spinner";
import SimpleBar from "simplebar-react";
import { useParams } from "react-router-dom";
import moment from "jalali-moment";
import axiosInstance from "api/axiosInstance";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  Copy,
  ThumbsUp,
  ThumbsDown,
  Bot,
} from "lucide-react";
import "./styles/clientChat.responsive.css";
import { useTranslation } from "react-i18next";
import { LuCornerDownRight } from "react-icons/lu";
/* -------------------------------------------------------------------------- */
/*                    Streaming (typewriter) effect for bot text              */
/* -------------------------------------------------------------------------- */

const useStreamingText = (text, animate, onTick) => {
  const [displayed, setDisplayed] = useState(animate ? "" : text);
  const [isDone, setIsDone] = useState(!animate);
  const hasAnimatedRef = useRef(!animate);

  useEffect(() => {
    if (!animate || hasAnimatedRef.current) {
      setDisplayed(text);
      setIsDone(true);
      hasAnimatedRef.current = true;
      return;
    }

    const words = text.split(/(\s+)/);
    let idx = 0;
    let timeoutId;

    const tick = () => {
      idx += 1;
      setDisplayed(words.slice(0, idx).join(""));
      onTick?.();

      if (idx >= words.length) {
        hasAnimatedRef.current = true;
        setIsDone(true); // 👈 رندر جدید trigger می‌کنه
        return;
      }

      timeoutId = setTimeout(tick, 30 + Math.random() * 35);
    };

    timeoutId = setTimeout(tick, 30);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animate]);

  return { displayed, isDone };
};

const StreamingResponse = ({ text, animate, onTick, onDone }) => {
  const { displayed, isDone } = useStreamingText(text, animate, onTick);
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (isDone && !notifiedRef.current) {
      notifiedRef.current = true;
      onDone?.();
    }
  }, [isDone, onDone]);

  return (
    <div className={animate && !isDone ? "streaming-cursor" : ""}>
      <MarkdownDisplay markdownText={displayed} />
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const ChatMessage = ({
  messages,
  chatLoading,
  roomId,
  onStreamingChange,
  onSuggestionClick, // 👈 جدید
  suggestionsDisabled = false, // 👈 جدید
}) => {
  const { botType } = useParams();
  const [loadingMap, setLoadingMap] = useState({ initial: true });
  const [isLoading, setIsLoading] = useState(true);
  const [streamDoneMap, setStreamDoneMap] = useState({});
  const accessToken = useSelector((state) => state.auth.accessToken);
  const scrollRef = useRef(null);
  const { username } = useParams();
  const buttonRefs = useRef({});
  const NEAR_BOTTOM_THRESHOLD = 120;
  const { i18n } = useTranslation();
  const isEnglish = (i18n.language === "en");
  const markStreamDone = (id) => {
    setStreamDoneMap((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };
  const isNearBottom = () => {
    if (!scrollRef.current) return true;
    const scrollElement = scrollRef.current.getScrollElement();
    if (!scrollElement) return true;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    return scrollHeight - (scrollTop + clientHeight) < NEAR_BOTTOM_THRESHOLD;
  };

  const scrollToBottom = (force = false) => {
    if (!scrollRef.current) return;
    const scrollElement = scrollRef.current.getScrollElement();
    if (!scrollElement) return;

    if (force || isNearBottom()) {
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  };
  useEffect(() => {
    messages.forEach(({ responseId }) => {
      if (responseId && !buttonRefs.current[responseId]) {
        const keys = ["copy", "like", "dislike"];
        keys.forEach((key) => {
          const btn = document.getElementById(`btn-${responseId}-${key}`);
          if (btn) {
            btn.classList.add("animate-pulse-heart");
            btn.addEventListener(
              "animationend",
              () => btn.classList.remove("animate-pulse-heart"),
              { once: true }
            );
          }
        });
        buttonRefs.current[responseId] = true;
      }
    });
  }, [messages]);

  const buildMsgStatuses = (msgs) => {
    const map = {};
    msgs.forEach((msg) => {
      if (msg.responseId) {
        map[msg.responseId] = msg.status ?? null;
      }
    });
    return map;
  };

  const [msgStatuses, setMsgStatuses] = useState(() =>
    buildMsgStatuses(messages)
  );

  useEffect(() => {
    const timeout = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timeout);
  }, []);
  useEffect(() => {
    if (isLoading) return;

    let rafId;
    let attempts = 0;

    const tryScroll = () => {
      if (!scrollRef.current) return;
      const scrollElement = scrollRef.current.getScrollElement();
      if (!scrollElement) return;

      scrollElement.scrollTop = scrollElement.scrollHeight;

      attempts += 1;
      if (attempts < 5) {
        rafId = requestAnimationFrame(tryScroll);
      }
    };

    rafId = requestAnimationFrame(tryScroll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  useEffect(() => {
    const timeoutId = setTimeout(() => scrollToBottom(true), 100);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  useEffect(() => {
    const newLoadingState = {};

    if (messages.length === 0) {
      newLoadingState["initial"] = true;
    } else {
      messages.forEach(({ message, response }) => {
        if ((!message || message === "") && !response) {
          newLoadingState["initial"] = true;
        } else if (message && !response) {
          newLoadingState[message] = true;
        } else if (message === "" && response === "") {
          newLoadingState[message] = false;
        } else if (chatLoading) {
          newLoadingState[message] = false;
        } else {
          newLoadingState[message] = false;
        }
      });
    }

    setLoadingMap(newLoadingState);
  }, [messages, chatLoading]);

  useEffect(() => {
    setMsgStatuses((prev) => {
      const next = { ...prev };
      messages.forEach(({ responseId, status }) => {
        if (responseId && status != null && !(responseId in next)) {
          next[responseId] = status;
        }
      });
      return next;
    });
  }, [messages]);
  useEffect(() => {
    const hasNewStreamingMessage = messages.some((msg) => msg.isNew);
    if (hasNewStreamingMessage) {
      onStreamingChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  const handleLikes = async (event) => {
    try {
      const responseId = event.currentTarget.getAttribute("data-response-id");
      const key = event.currentTarget.getAttribute("data-action");
      const currentStatus = msgStatuses[responseId];
      const response = await axiosInstance.post(
        "/business/chats/messageStatus",
        {
          roomId,
          messageId: responseId,
          status: currentStatus === key ? null : key,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.data.ok) {
        switch (response.data.data.likeStatus) {
          case "like":
            toast.success("نظر شما با موفقیت ثبت شد.");
            break;
          case "dislike":
            toast.error("نظر شما با موفقیت ثبت شد.");
            break;
          default:
            toast.warn("نظر شما با موفقیت ثبت شد.");
            break;
        }
        setMsgStatuses((prev) => ({
          ...prev,
          [responseId]: response.data.data.likeStatus,
        }));
      }
    } catch (error) {
      console.error("Error while changing message status: ", error);
      if (error.response) {
        const errorMessage = error.response.data?.error || error?.message;
        toast.error(errorMessage || "مشکل شبکه یا عدم دسترسی به سرور");
      } else {
        toast.error("مشکل شبکه یا عدم دسترسی به سرور");
      }
    }
  };

  const buttons = [
    {
      key: "copy",
      svg: <Copy className="w-[17px] h-[17px]" strokeWidth={1.7} />,
      onClick: (event) => {
        const responseId = event.currentTarget.getAttribute("data-response-id");
        const el = document.querySelector(`#msg-${responseId}`);
        if (el) {
          navigator.clipboard.writeText(el.textContent);
          toast.success("متن کپی شد.");
        }
      },
    },
    { key: "like", svg: <ThumbsUp className="w-[17px] h-[17px]" strokeWidth={1.7} />, onClick: handleLikes },
    { key: "dislike", svg: <ThumbsDown className="w-[17px] h-[17px]" strokeWidth={1.7} />, onClick: handleLikes },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-[60px] h-[60px] rounded-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center text-white shadow-lg shadow-teal-500/40 ring-1 ring-teal-300/40">
            <Bot className="w-7 h-7 relative z-10" />
            <span className="absolute inset-0 rounded-full bg-teal-400/40 animate-ping" />
            <div className="absolute -inset-2 rounded-full bg-teal-500/20 blur-xl pointer-events-none" />
          </div>

          <p className="text-sm font-medium bg-gradient-to-l from-teal-600 to-cyan-600 dark:!from-teal-400 dark:!to-cyan-400 bg-clip-text text-transparent animate-pulse">
            در حال بارگذاری...
          </p>
        </div>
      </div>
    );
  }

  const heartAnimationStyle = `
    @keyframes pulse-heart {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.3); }
    }
    .animate-pulse-heart {
      animation: pulse-heart 0.6s ease-in-out;
    }
  `;

  return (
    <SimpleBar ref={scrollRef} className="h-full">
      <style>{heartAnimationStyle}</style>

      <div className="min-h-full w-full">
        <div className="chat-msg-wrapper mx-auto w-full max-w-[850px]">
          {loadingMap["initial"] && (
            <div className="flex items-center gap-2 py-4">
              <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-500/40 ring-1 ring-teal-300/40">
                <Bot className="w-4 h-4" />
                <span className="absolute inset-0 rounded-full bg-teal-400/40 animate-ping" />
              </div>
              <PulseLoader color="#14b8a6" size={6} />
            </div>
          )}

          {messages.map(
            (
              {
                message,
                response,
                is_sent_by_user,
                createdAt,
                debug,
                responseId,
                isNew,
                suggestions = [], // 👈 جدید
                suggestions_consumed = false, // 👈 جدید
              },
              index
            ) => (
              <div key={responseId || `${createdAt}-${message}-${index}`} className="w-full">
                {/* ================= USER MESSAGE ================= */}
                {message && (
                  <div className="flex justify-start py-5 ">
                    <div className="flex w-full items-start gap-3">
                      <div
                        className="
                        chat-bubble-user inline-block
                        px-4 py-2.5
                        rounded-2xl rounded-tr-md
                        bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-700
                        dark:!from-teal-600 dark:!via-teal-700 dark:!to-cyan-800
                        text-white
                        text-[13px] md:text-[15px] leading-7
                        whitespace-pre-wrap break-words text-start
                        shadow-lg shadow-teal-500/30
                        dark:!shadow-teal-900/50
                        ring-1 ring-teal-300/40 dark:!ring-teal-400/30
                        transition-all duration-300 ease-out
                        hover:shadow-xl hover:shadow-teal-500/50
                        hover:ring-teal-200/60
                        hover:-translate-y-0.5
                        relative overflow-hidden
                        before:absolute before:inset-0
                        before:bg-gradient-to-b before:from-white/20 before:to-transparent
                        before:pointer-events-none
                      "
                      >
                        <span className={`relative z-10 ${isEnglish && "font-sans"}`}>{message}</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* ================= DEBUG ================= */}
                {debug && (
                  <div className="flex gap-3 py-3">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-yellow-500 flex items-center justify-center text-white">
                      <Bot className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-medium text-yellow-600 mb-1">Debug</div>
                      <div className="text-sm text-gray-600 dark:!text-gray-300">{debug}</div>
                    </div>
                  </div>
                )}

                {/* ================= BOT LOADING ================= */}
                {loadingMap[message] && !chatLoading && !response && (
                  <>
                    <style>{`
      @keyframes barWave {
        0%, 100% { transform: scaleY(0.35); opacity: 0.5; }
        50%      { transform: scaleY(1);    opacity: 1;   }
      }
      @keyframes bubbleIn {
        from { opacity: 0; transform: translateY(6px) scale(0.96); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      .typing-bar {
        animation: barWave 1.1s ease-in-out infinite;
        transform-origin: center;
      }
      .typing-bubble {
        animation: bubbleIn 0.28s ease-out;
      }
    `}</style>

                    <div className="flex gap-3 items-center py-4 flex-row-reverse">
                      <div className="relative w-8 h-8 shrink-0">
                        <div className="absolute  inset-0 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 blur-[6px] opacity-60" />
                        <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-[#aadec5] to-teal-300 flex items-center justify-center text-[#1B4748] ring-1 ring-white/60 dark:ring-teal-300/30 shadow-[0_4px_12px_-4px_rgba(20,184,166,0.6)]">
                          <Bot className="w-4 h-4" />
                        </div>
                      </div>

                      <div className="typing-bubble relative flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-tr-md
        bg-gradient-to-br from-white/85 to-teal-50/70
        dark:from-teal-900/70 dark:to-teal-800/50
        backdrop-blur-md
        border border-white/70 dark:border-teal-300/20
        shadow-[0_8px_22px_-10px_rgba(20,184,166,0.55)]
      ">
                        {[0, 0.15, 0.3, 0.45].map((delay, i) => (
                          <span
                            key={i}
                            className="typing-bar inline-block w-[3px] h-4 rounded-full bg-gradient-to-b from-yellow-300 via-teal-400 to-teal-600 dark:from-yellow-300 dark:via-teal-300 dark:to-teal-500"
                            style={{ animationDelay: `${delay}s` }}
                          />
                        ))}
                        <span className="pointer-events-none absolute inset-0 rounded-2xl rounded-tr-md bg-gradient-to-r from-yellow-200/20 via-transparent to-teal-300/20" />
                      </div>
                    </div>
                  </>
                )}

                {/* ================= BOT RESPONSE ================= */}
                {response && (
                  <div className="group flex gap-3 py-5 flex-row-reverse">

                    <div className="flex-1 min-w-0">
                      <div
                        id={`msg-${responseId}`}
                        className={`text-[13px] md:text-[15px] leading-10 ${isEnglish && "font-sans"} text-gray-800 dark:!text-gray-300 dark:[&_a]:text-gray-800`}
                      >
                        <StreamingResponse
                          text={response}
                          animate={!!isNew}
                          onTick={() => scrollToBottom(false)}
                          onDone={() => {
                            markStreamDone(responseId);
                            onStreamingChange?.(false);
                          }}
                        />
                      </div>

                      {/* ================= SUGGESTIONS ================= */}
                      <div className="flex justify-between flex-col lg:flex-row">
                        {Array.isArray(suggestions) &&
                          suggestions.length > 0 &&
                          !suggestions_consumed &&
                          streamDoneMap[responseId] && (
                            <div
                              className="flex flex-wrap justify-start gap-2 mt-3"
                              dir="auto"
                            >
                              {suggestions
                                .filter(
                                  (suggestion) =>
                                    typeof suggestion === "string" &&
                                    suggestion.trim() !== ""
                                )
                                .map((suggestion, suggestionIndex) => (
                                  <button
                                    key={`${responseId}-suggestion-${suggestionIndex}`}
                                    type="button"
                                    disabled={suggestionsDisabled || !responseId}
                                    onClick={() =>
                                      onSuggestionClick?.(suggestion, responseId)
                                    }
                                    className="
                                    flex
                                    items-center
                                    gap-1
                                    max-w-full
                                    px-3.5 py-2
                                    rounded-xl
                                    text-xs font-medium
                                    text-teal-700 dark:!text-teal-300
                                    bg-teal-50 dark:!bg-teal-900/25
                                    ring-1 ring-teal-400/30 dark:!ring-teal-400/20
                                    text-right break-words
                                    transition-all duration-200
                                    hover:bg-teal-100 dark:!hover:bg-teal-900/40
                                    hover:ring-teal-400/50
                                    hover:shadow-sm hover:shadow-teal-500/20
                                    active:scale-[0.98]
                                    disabled:opacity-50
                                    disabled:cursor-not-allowed
                                  "
                                  >
                                    <LuCornerDownRight />
                                    {suggestion}
                                  </button>
                                ))}
                            </div>
                          )}

                        {response &&
                          message !== "" &&
                          responseId &&
                          botType !== "RayaBot" &&
                          botType !== "supportBot" && (
                            <div
                              className="
        flex items-center justify-end gap-1 mt-2
        opacity-60
        transition-opacity duration-200
        group-hover:opacity-100
      "
                            >
                              {buttons.map(({ key, svg, onClick }) => {
                                const isActive = msgStatuses[responseId] === key;

                                return (
                                  <button
                                    key={key}
                                    id={`btn-${responseId}-${key}`}
                                    data-response-id={responseId}
                                    data-action={key}
                                    onClick={onClick}
                                    title={
                                      key === "copy"
                                        ? "کپی"
                                        : key === "like"
                                          ? "پسندیدم"
                                          : "نپسندیدم"
                                    }
                                    className={`
              p-1.5
              rounded-lg
              transition-all
              duration-200

              ${isActive
                                        ? "bg-teal-50 dark:!bg-teal-900/30 text-teal-600 dark:!text-teal-400"
                                        : "text-gray-400 dark:!text-zinc-500 hover:text-teal-600 dark:hover:!text-teal-400 hover:bg-teal-50 dark:hover:!bg-teal-900/20"
                                      }

              hover:scale-110
              active:scale-95
            `}
                                  >
                                    {svg}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                      </div>

                    </div>
                  </div>
                )}
                {/* ================= MESSAGE WITHOUT RESPONSE ================= */}
                {!response && !is_sent_by_user && message && (
                  <div className="flex gap-3 py-5 flex-row-reverse">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-[#aadec5] flex items-center justify-center text-[#1B4748]">
                      <Bot className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-800 dark:!text-gray-200">
                          {username || "بات"}
                        </span>
                        <span className={`text-[10px] ${isEnglish && "font-sans"} text-gray-400`}>
                          {moment.utc(createdAt).local().locale("fa").format("HH:mm")}
                        </span>
                      </div>
                      <div className="text-[15px] leading-7 text-gray-800 dark:!text-gray-100">
                        <MarkdownDisplay markdownText={message} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </SimpleBar>
  );
};

export default ChatMessage;